import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";

import { Container } from "@/components/ui/Container";
import { Button } from "@/components/ui/Button";
import { HERO, WHO_WE_SERVE } from "@/data/content";
import { fadeUp, staggerContainer, staggerItem, EASE } from "@/lib/motion";

export function Hero() {
  return (
    <section
      className="relative isolate overflow-hidden border-b border-border bg-muted"
      data-testid="hero-section"
    >
      <Container className="grid grid-cols-1 items-center gap-12 pb-20 pt-32 sm:pt-36 lg:grid-cols-12 lg:gap-8 lg:pb-28 lg:pt-44">
        {/* Copy */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="lg:col-span-7"
        >
          <motion.h1
            variants={staggerItem}
            className="max-w-3xl text-4xl font-bold leading-[1.1] tracking-tight text-foreground sm:text-5xl lg:text-[3.4rem] text-balance"
            data-testid="hero-headline"
          >
            {HERO.headline}
          </motion.h1>

          <motion.p
            variants={staggerItem}
            className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg"
            data-testid="hero-subcopy"
          >
            {HERO.subheadline}
          </motion.p>

          <motion.div
            variants={staggerItem}
            className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <Button
              as="link"
              to={HERO.primaryCta.to}
              size="lg"
              data-testid="hero-primary-button"
            >
              {HERO.primaryCta.label}
              <ArrowRight size={18} />
            </Button>
            <Button
              as="a"
              href={HERO.secondaryCta.to}
              size="lg"
              variant="outline"
              data-testid="hero-secondary-button"
            >
              {HERO.secondaryCta.label}
            </Button>
          </motion.div>

          <motion.ul
            variants={staggerItem}
            className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-x-6"
          >
            {HERO.highlights.map((h) => (
              <li key={h} className="flex items-center gap-2 text-sm text-foreground/80">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Check size={13} strokeWidth={3} />
                </span>
                {h}
              </li>
            ))}
          </motion.ul>
        </motion.div>

        {/* Trust panel */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.25, duration: 0.5, ease: EASE }}
          className="lg:col-span-5"
        >
          <div className="rounded-2xl border border-border bg-background p-6 shadow-sm sm:p-8">
            <p className="text-sm font-semibold uppercase tracking-wider text-accent">
              Who we support
            </p>
            <div className="mt-5 space-y-4">
              {WHO_WE_SERVE.map((item) => (
                <div key={item.title} className="flex items-center gap-3">
                  <span className="text-sm font-medium text-foreground">{item.title}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-xl bg-muted p-4">
              <p className="text-sm leading-relaxed text-muted-foreground">
                Connecting suitable study opportunities with capable clinical
                sites — built for practical, quality-focused operations.
              </p>
            </div>
          </div>
        </motion.div>
      </Container>
    </section>
  );
}
